import React, { memo, useCallback, useContext, useRef } from 'react'
import { IconButton, TextField } from '@mui/material'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { ClipboardProps } from './Clipboard.types'
import { CopyIcon } from './Icons'

function Clipboard({
    value,
    label,
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    color = 'info',
    fullWidth = true,
    readOnly = true,
}: ClipboardProps) {
    const feedbackContext = useContext(FeedbackContext)
    const inputRef = useRef<HTMLInputElement>(null)

    const copyToClipboard = useCallback(() => {
        if (!inputRef.current) {
            feedbackContext.showWarning(
                translate('clipboard.no_input') as string
            )
            return
        }

        if (navigator.userAgent.match(/ipad|iphone/i)) {
            const range = document.createRange()
            range.selectNodeContents(inputRef.current)

            const selection = window.getSelection()
            if (!selection) {
                feedbackContext.showWarning(
                    translate('clipboard.no_selection') as string
                )
                return
            }

            selection.removeAllRanges()
            selection.addRange(range)
            inputRef.current.setSelectionRange(0, 999999)
        } else {
            inputRef.current.select()
        }

        document.execCommand('copy')
        feedbackContext.showSuccess(translate('clipboard.copied') as string)
    }, [feedbackContext])

    return (
        <TextField
            variant={variant}
            margin={margin}
            size={size}
            value={value}
            label={label}
            inputRef={inputRef}
            InputProps={{
                endAdornment: (
                    <IconButton color={color} onClick={copyToClipboard}>
                        <CopyIcon />
                    </IconButton>
                ),
                readOnly,
            }}
            fullWidth={fullWidth}
        />
    )
}

export default memo(Clipboard)
